import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/magazzino/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const empty = { name: "", email: "", phone: "", address: "", notes: "" };

export default function SuppliersPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => { const { data } = await api.get("/fornitori"); setItems(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/fornitori/${editing.id}`, form);
      else await api.post("/fornitori", form);
      toast.success(editing ? "Fornitore aggiornato" : "Fornitore creato");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Eliminare "${s.name}"?`)) return;
    await api.delete(`/fornitori/${s.id}`); toast.success("Fornitore eliminato"); load();
  };

  return (
    <div className="space-y-6 lg:flex lg:h-full lg:min-h-0 lg:flex-col lg:gap-6 lg:space-y-0">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button data-testid="new-supplier-btn" className="h-10 bg-slate-900 hover:bg-slate-800 text-white">
              <Plus weight="bold" size={16} className="mr-1.5"/> Nuovo fornitore
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-xl">
            <DialogHeader><DialogTitle className="font-display tracking-tight">{editing ? "Modifica fornitore" : "Nuovo fornitore"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-600">Ragione sociale *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="supplier-name-input"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600">Email</Label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="supplier-email-input"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600">Telefono</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" data-testid="supplier-phone-input"/>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-600">Indirizzo</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" data-testid="supplier-address-input"/>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-600">Note</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" data-testid="supplier-notes-input"/>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button onClick={save} data-testid="supplier-save-btn" className="bg-slate-900 hover:bg-slate-800 text-white">Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 rounded-md shadow-none overflow-auto lg:flex-1 lg:min-h-0" data-testid="suppliers-table">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50">
            <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50">
              <th className="px-5 py-3 font-medium">Fornitore</th>
              <th className="px-5 py-3 font-medium">Email</th>
              <th className="px-5 py-3 font-medium">Telefono</th>
              <th className="px-5 py-3 font-medium">Indirizzo</th>
              <th className="px-5 py-3 font-medium text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">Nessun fornitore.</td></tr>}
            {items.map((s) => (
              <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-5 py-3 font-medium">{s.name}</td>
                <td className="px-5 py-3 text-slate-600">{s.email || "—"}</td>
                <td className="px-5 py-3 tabular text-slate-600">{s.phone || "—"}</td>
                <td className="px-5 py-3 text-slate-600 truncate max-w-[280px]">{s.address || "—"}</td>
                <td className="px-5 py-3 text-right">
                  <button className="w-8 h-8 rounded-md hover:bg-slate-100 text-slate-600 inline-grid place-items-center"
                    onClick={() => { setEditing(s); setForm({ name: s.name, email: s.email, phone: s.phone, address: s.address, notes: s.notes }); setOpen(true); }}
                    data-testid={`edit-sup-${s.name}`}><PencilSimple size={16}/></button>
                  <button className="w-8 h-8 rounded-md hover:bg-red-50 text-red-600 inline-grid place-items-center"
                    onClick={() => remove(s)} data-testid={`delete-sup-${s.name}`}><Trash size={16}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
